import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Divider from "antd/es/divider";
import Row from "antd/es/row";
import { FormattedMessage } from "react-intl";

import { Deal, EntityType } from "@mapmycustomers/shared/types/entity";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import Group from "@mapmycustomers/shared/types/entity/Group";
import User from "@mapmycustomers/shared/types/User";
import ColumnModel from "@mapmycustomers/shared/types/viewModel/internalModel/ColumnModel";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";

import DownloadEntitiesModal from "@app/component/DownloadEntitiesModal";
import SimpleEntityMap from "@app/component/map/SimpleEntityMap";
import StagesLegend from "@app/component/StagesLegend";
import TextWithLine from "@app/component/typography/TextWithLine";
import ReportType from "@app/enum/ReportType";
import StandardHeader from "@app/scene/reports/components/Header/StandardHeader";
import { getFunnels, getFunnelStages } from "@app/store/deal";
import { showEntityView } from "@app/store/entityView/actions";
import { getCompanyGroups } from "@app/store/groups";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { PREVIEW_MODAL_LIMIT } from "@app/util/consts";

import MapFilter from "../../enums/MapFilter";
import reportsAnalytics, {
  getFeaturePathByMapFilter,
  reportsDealsAnalytics,
} from "../../reportsAnalytics";
import {
  downloadReportEntities,
  fetchDealsByStage,
  fetchDealsMapData,
  fetchFunnelsStats,
  fetchStageStats,
} from "../../store/actions";
import DealsMapData from "../../store/DealsMapData";
import FunnelStatsData from "../../store/FunnelStatsData";
import { getDealsMapData, getFunnelsStatsData, isLoading } from "../../store/selectors";
import StageStatsData from "../../store/StageStatsData";
import useUserIds from "../../utils/useUserIds";
import BaseScreenProps from "../BaseScreenProps";
import DealsMapFilter from "../MapFilters/DealsMapFilter";

import DealsByGeographyChart from "./components/Charts/GeographyChart";
import styles from "./DealsCard.module.scss";
import FunnelSelectBar from "./FunnelSelectBar";
import FunnelStages from "./FunnelStages";
import StatsForStage from "./StatsForStage";

interface Props extends BaseScreenProps {
  companyGroups: Group[];
  dealsMapData: DealsMapData;
  downloadReportEntities: typeof downloadReportEntities;
  fetchDealsByStageId: typeof fetchDealsByStage.request;
  fetchDealsMapData: typeof fetchDealsMapData.request;
  fetchFunnelsStats: typeof fetchFunnelsStats.request;
  fetchStageStats: typeof fetchStageStats.request;
  funnels: Funnel[];
  funnelsStatsData: FunnelStatsData;
  funnelStages: Record<Funnel["id"], Stage[]>;
  loading: boolean;
  onShowEntityView: typeof showEntityView;
  users: User[];
}

const DealsCard: React.FC<Props> = ({
  dealsMapData,
  downloadReportEntities,
  fetchDealsByStageId,
  fetchDealsMapData,
  fetchFunnelsStats,
  fetchStageStats,
  funnels,
  funnelsStatsData,
  funnelStages,
  loading,
  onShowEntityView,
  selectedUsers,
}) => {
  const userIds: Array<User["id"]> = useUserIds(selectedUsers);

  const [selectedFunnel, setSelectedFunnel] = useState<Funnel | undefined>(undefined);
  const [stageStatsData, setStageStatsData] = useState<StageStatsData | undefined>();
  const [selectedStage, setSelectedStage] = useState<Stage | undefined>();
  const [columnSelectorVisible, showColumnSelector, hideColumnSelector] = useBoolean();
  useEffect(() => {
    if (selectedStage) {
      fetchDealsByStageId({ limit: PREVIEW_MODAL_LIMIT, stageId: selectedStage.id, userIds });
    }
  }, [fetchDealsByStageId, selectedStage, userIds]);

  useEffect(() => {
    fetchStageStats({
      callback: setStageStatsData,
      funnelId: selectedFunnel?.id,
      stageId: selectedStage?.id,
    });
  }, [fetchStageStats, selectedFunnel?.id, selectedStage?.id]);

  useChangeTracking(() => {
    reportsAnalytics.extend("Deals").clicked(["Change Funnel"], { funnel: selectedFunnel });
  }, [selectedFunnel]);

  useChangeTracking(() => {
    reportsAnalytics.extend("Deals").clicked(["Change Funnel Stage"], { stage: selectedStage });
  }, [selectedStage]);

  // ========== handle opening of deal preview pane ========== //
  const handlePreviewClick = useCallback(
    (deal: Deal) => {
      onShowEntityView({ entityId: deal.id, entityType: EntityType.DEAL });
      reportsDealsAnalytics.clicked(["Open Individual Pin"], { deal });
    },
    [onShowEntityView]
  );

  // ========== map vars ========== //
  const [mapFilter, setMapFilter] = useState<MapFilter>(MapFilter.OPEN_DEALS);

  useChangeTracking(
    () => reportsDealsAnalytics.clicked(["Smart Filter", ...getFeaturePathByMapFilter(mapFilter)]),
    [mapFilter]
  );

  // downloads all pins seen on map (with current filter)
  const handleDownloadPins = useCallback(
    (columns: ColumnModel) => {
      downloadReportEntities({
        columns,
        entityType: EntityType.DEAL,
        ids: dealsMapData.deals
          .filter((deal) => !!deal.account && !!deal.account.geoPoint)
          .map(({ id }) => id),
      });
    },
    [downloadReportEntities, dealsMapData.deals]
  );

  useEffect(() => {
    if (selectedFunnel) {
      fetchDealsMapData({
        filter: mapFilter,
        funnelId: selectedFunnel.id,
        stageId: selectedStage?.id,
        userIds,
      });
    }
  }, [fetchDealsMapData, selectedFunnel, selectedStage?.id, userIds, mapFilter]);

  // ===================== updates map (but does not fetch new data) ===================== //
  useEffect(() => {
    if (!selectedFunnel && funnels.length) {
      setSelectedFunnel(funnels[0]); // default funnel to show
    }
  }, [funnels, selectedFunnel, setSelectedFunnel]);

  // ===================== fetch statistics for deal from stats and summary endpoints ===================== //
  useEffect(() => {
    if (selectedFunnel) {
      fetchFunnelsStats({ funnel: selectedFunnel, userIds });
    }
  }, [fetchFunnelsStats, selectedFunnel, userIds]);

  return (
    <div className={styles.container}>
      <StandardHeader
        extra={
          <FunnelSelectBar funnels={funnels} onChange={setSelectedFunnel} value={selectedFunnel} />
        }
        reportType={ReportType.DEALS}
      />

      <Divider />

      <div className={styles.funnelContainer}>
        <FunnelStages
          loading={loading}
          onChange={setSelectedStage}
          stages={selectedFunnel ? funnelStages[selectedFunnel.id] ?? [] : []}
          value={selectedStage}
        />

        {/* -------------- pipeline metrics & charts -------------- */}
        <StatsForStage
          funnelsStatsData={funnelsStatsData}
          selectedFunnel={selectedFunnel}
          selectedStage={selectedStage}
          stageStatsData={stageStatsData}
          userIds={userIds}
        />

        {/* -------------- deals point map -------------- */}
        <Row className={styles.map}>
          <Col span={16}>
            <Row className={styles.mapContainer}>
              <Col span={24}>
                <TextWithLine>
                  <FormattedMessage
                    id="reports.map.header.pins"
                    defaultMessage="Pins Showing ({count})"
                    description="Title for Pins in Pins Showing section on Last Location screen."
                    values={{
                      count: dealsMapData.total,
                    }}
                  />
                </TextWithLine>
                <SimpleEntityMap<Deal>
                  entities={dealsMapData.deals}
                  entityType={EntityType.DEAL}
                  loading={dealsMapData.loading}
                  onPreview={handlePreviewClick}
                  showBaseLayerSelector
                />
              </Col>
            </Row>
            <Row>
              <StagesLegend
                className={styles.stagesLegend}
                stages={selectedFunnel ? funnelStages[selectedFunnel.id] ?? [] : []}
              />
            </Row>
          </Col>
          <Col className={styles.mapFiltersContainer} span={6}>
            <DealsMapFilter
              filter={mapFilter}
              onChange={setMapFilter}
              onDownload={showColumnSelector}
            />
          </Col>
        </Row>

        {/* -------------- deals by geography map -------------- */}
        <DealsByGeographyChart funnelId={selectedFunnel?.id} userIds={userIds} />
      </div>
      {columnSelectorVisible && (
        <DownloadEntitiesModal
          entityType={EntityType.DEAL}
          hideInformation
          onDownload={handleDownloadPins}
          onHide={hideColumnSelector}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  companyGroups: getCompanyGroups(state),
  dealsMapData: getDealsMapData(state),
  funnels: getFunnels(state),
  funnelsStatsData: getFunnelsStatsData(state),
  funnelStages: getFunnelStages(state),
  loading: isLoading(state),
  users: getUsers(state),
});

const mapDispatchToProps = {
  downloadReportEntities,
  fetchDealsByStageId: fetchDealsByStage.request,
  fetchDealsMapData: fetchDealsMapData.request,
  fetchFunnelsStats: fetchFunnelsStats.request,
  fetchStageStats: fetchStageStats.request,
  onShowEntityView: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(DealsCard);
